import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Switch,
  Button,
  Space,
  Divider,
  Typography,
  Alert,
  Spin,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  ApiOutlined,
  DatabaseOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import EhrmConfigApi from '../../api/settings/EhrmConfigApi';

const { Title, Text } = Typography;
const { Password } = Input;

const EhrmConfig = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // Load existing configuration on component mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const existingConfig = await EhrmConfigApi.getConfig();
      if (existingConfig) {
        setConfig(existingConfig);
        // Set form values with masked passwords
        form.setFieldsValue({
          enabled: existingConfig.enabled,
          baseUrl: existingConfig.baseUrl,
          apiKey: '', // Don't populate masked API key
          dbServer: existingConfig.dbServer,
          dbName: existingConfig.dbName,
          dbUsername: existingConfig.dbUsername,
          dbPassword: '', // Don't populate masked password
        });
      } else {
        // No config exists, start in editing mode
        setIsEditing(true);
        form.setFieldsValue({
          enabled: false,
          baseUrl: '',
          apiKey: '',
          dbServer: '',
          dbName: '',
          dbUsername: '',
          dbPassword: '',
        });
      }
    } catch (error) {
      console.error('Failed to load EHRM config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setLoading(true);
    try {
      // Only include fields that have values (don't send empty passwords)
      const configData = {
        enabled: Boolean(values.enabled), // Ensure boolean conversion
        baseUrl: values.baseUrl || undefined,
        apiKey: values.apiKey || undefined,
        dbServer: values.dbServer || undefined,
        dbName: values.dbName || undefined,
        dbUsername: values.dbUsername || undefined,
        dbPassword: values.dbPassword || undefined,
      };

      console.log('Saving EHRM config with data:', configData); // Debug log

      let result;
      if (config) {
        // Update existing config
        result = await EhrmConfigApi.updateConfig(configData);
      } else {
        // Create new config
        result = await EhrmConfigApi.createConfig(configData);
      }

      setConfig(result);
      setIsEditing(false);
      
      // Reload to get the latest masked values
      await loadConfig();
    } catch (error) {
      console.error('Failed to save EHRM config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      const values = await form.validateFields(['baseUrl', 'apiKey']);
      setTestLoading(true);

      const testData = {
        baseUrl: values.baseUrl,
        apiKey: values.apiKey,
        dbServer: values.dbServer || config?.dbServer,
        dbName: values.dbName || config?.dbName,
        dbUsername: values.dbUsername || config?.dbUsername,
        dbPassword: values.dbPassword || config?.dbPassword,
      };

      await EhrmConfigApi.testConnection(testData);
    } catch (error) {
      console.error('Connection test failed:', error);
    } finally {
      setTestLoading(false);
    }
  };

  const handleTestDatabaseConnection = async () => {
    try {
      const values = await form.validateFields(['dbServer', 'dbName', 'dbUsername', 'dbPassword']);
      setTestLoading(true);

      const testData = {
        baseUrl: values.baseUrl || config?.baseUrl,
        apiKey: values.apiKey || config?.apiKey,
        dbServer: values.dbServer,
        dbName: values.dbName,
        dbUsername: values.dbUsername,
        dbPassword: values.dbPassword,
      };

      await EhrmConfigApi.testConnection(testData);
    } catch (error) {
      console.error('Database connection test failed:', error);
    } finally {
      setTestLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    if (config) {
      form.setFieldsValue({
        enabled: config.enabled,
        baseUrl: config.baseUrl,
        apiKey: '',
        dbServer: config.dbServer,
        dbName: config.dbName,
        dbUsername: config.dbUsername,
        dbPassword: '',
      });
    }
  };

  if (loading && !config) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>Loading EHRM configuration...</div>
        </div>
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px', height: '100vh', overflow: 'auto' }}>
      <Card
        title={
          <Space>
            <ApiOutlined />
            <span>EHRM Integration Configuration</span>
          </Space>
        }
        extra={
          !isEditing && (
            <Button type="primary" onClick={() => setIsEditing(true)}>
              Edit Configuration
            </Button>
          )
        }
      >
      <Alert
        message="EHRM Integration Settings"
        description="Configure the connection to your EHRM (Employee Human Resource Management) system for seamless data synchronization."
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        disabled={!isEditing}
      >
        <Row gutter={[16, 0]}>
          <Col span={24}>
            <Form.Item
              name="enabled"
              valuePropName="checked"
            >
              <Space>
                <Switch 
                  checked={form.getFieldValue('enabled')}
                  onChange={(checked) => form.setFieldsValue({ enabled: checked })}
                />
                <Text strong>Enable EHRM Integration</Text>
                <Tooltip title="Toggle to enable or disable EHRM integration system-wide">
                  <ExclamationCircleOutlined style={{ color: '#1890ff' }} />
                </Tooltip>
              </Space>
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">
          <Space>
            <ApiOutlined />
            <span>API Configuration</span>
          </Space>
        </Divider>

        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              name="baseUrl"
              label="EHRM API Base URL"
              rules={[
                { required: true, message: 'Please enter the EHRM API base URL' },
                { type: 'url', message: 'Please enter a valid URL' },
              ]}
            >
              <Input
                placeholder="https://your-ehrm-server.com/api"
                prefix={<ApiOutlined />}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="apiKey"
              label={
                <Space>
                  <span>API Key</span>
                  {config && config.apiKeyMasked && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      (Current: {config.apiKeyMasked})
                    </Text>
                  )}
                </Space>
              }
              rules={[
                { required: !config, message: 'Please enter the API key' },
              ]}
            >
              <Password
                placeholder={config ? "Leave empty to keep current key" : "Enter API key"}
                iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left">
          <Space>
            <DatabaseOutlined />
            <span>Database Configuration</span>
          </Space>
        </Divider>

        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              name="dbServer"
              label="Database Server"
              rules={[
                { required: true, message: 'Please enter the database server' },
              ]}
            >
              <Input
                placeholder="DESKTOP-5VJOON5\SQLEXPRESS"
                prefix={<DatabaseOutlined />}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dbName"
              label="Database Name"
              rules={[
                { required: true, message: 'Please enter the database name' },
              ]}
            >
              <Input
                placeholder="Enter database name"
                prefix={<DatabaseOutlined />}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 0]}>
          <Col span={12}>
            <Form.Item
              name="dbUsername"
              label="Database Username"
              rules={[
                { required: true, message: 'Please enter the database username' },
              ]}
            >
              <Input
                placeholder="root"
                prefix={<DatabaseOutlined />}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="dbPassword"
              label={
                <Space>
                  <span>Database Password</span>
                  {config && config.dbPasswordMasked && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      (Current: {config.dbPasswordMasked})
                    </Text>
                  )}
                </Space>
              }
              rules={[
                { required: !config, message: 'Please enter the database password' },
              ]}
            >
              <Password
                placeholder={config ? "Leave empty to keep current password" : "Enter database password"}
                iconRender={(visible) => (visible ? <EyeOutlined /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>
          </Col>
        </Row>

        {isEditing && (
          <>
            <Divider />
            <Row gutter={[16, 0]} justify="space-between">
              <Col>
                <Space>
                  <Button
                    icon={<CheckCircleOutlined />}
                    onClick={handleTestConnection}
                    loading={testLoading}
                    disabled={loading}
                  >
                    Test Connection
                  </Button>
                  <Button
                    icon={<DatabaseOutlined />}
                    onClick={handleTestDatabaseConnection}
                    loading={testLoading}
                    disabled={loading}
                  >
                    Test DB Connection
                  </Button>
                </Space>
              </Col>
              <Col>
                <Space>
                  <Button onClick={handleCancel} disabled={loading}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                  >
                    Apply Configuration
                  </Button>
                </Space>
              </Col>
            </Row>
          </>
        )}
      </Form>

      {config && !isEditing && (
        <>
          <Divider />
          <Alert
            message="Configuration Status"
            description={
              config.enabled
                ? "EHRM integration is enabled and configured."
                : "EHRM integration is configured but currently disabled."
            }
            type={config.enabled ? "success" : "warning"}
            showIcon
          />
        </>
      )}
      </Card>
    </div>
  );
};

export default EhrmConfig;
